import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./../MainHeader.module.scss";
import SlidingWidget, {CaptionButton, SlidingWidgetFooter} from "./SlidingWidget"
import { SearchWidget, TreeList, InlineLoader } from "@netapp/bxp-design-system-react"
import serviceConnectorMenuStyles from "./ConnectorMenu.module.scss";
import _ from "lodash";
import { DropdownArrowDownIcon, DropdownArrowUpIcon } from "@netapp/bxp-style/react-icons/Navigation"
import { CompanyIcon, FolderIcon } from "@netapp/bxp-style/react-icons/General"
import { useLocation } from 'react-router-dom';
import { recursiveSearch } from "modules/identity-access-management/utils";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../i18n/namespace";
import { useFeatureFlag } from "../../../../utils/useFeatureFlag";

const ScopeMenuFooter = ({selectedScope}) => {
    const dispatch = useDispatch();
    const activeScope = useSelector(state => state.tenancy.selectedScope);

    return <SlidingWidgetFooter onSwitch={() => {
        if(activeScope === selectedScope){
            return;
        }

        dispatch({
            type: "TENANCYV4:SET-SCOPE",
            payload: {
                scopeId: selectedScope.id
            },
            track: true
        });

        dispatch({
            type: "MENU:CLOSE-WIDGET"
        })
    }} onClose={() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        })
    }}/>
};



const MenuContent = React.memo(({setSelectedScope}) => {
    const activeScope = useSelector(state => state.tenancy.selectedScope);
    const selectedOrg = useSelector(state => state.tenancy.selectedOrg);
    const orgs = useMemo(() => selectedOrg ? [selectedOrg] : [], [selectedOrg]);
    const [activeFilter, setActiveFilter] = useState("");
    const { t } = useTranslation(i18nNamespace);

    // Filter to keep only organizations and folders (exclude fleets)
    const filterFoldersOnly = useCallback((scopes) => {
        const filterRecursive = (items) => {
            if (!items || !items.length) return [];
            return items.reduce((acc, scope) => {
                const resourceType = _.toLower(scope.resourceType);
                // Keep organization and folder, exclude project
                if (resourceType === "organization" || resourceType === "folder") {
                    const filteredScope = { ...scope };
                    if (scope.childScopes?.length) {
                        filteredScope.childScopes = filterRecursive(scope.childScopes);
                    }
                    acc.push(filteredScope);
                }
                return acc;
            }, []);
        };
        return filterRecursive(scopes);
    }, []);

    const filteredScopes = useMemo(() => {
        let scopesData;
        if(activeFilter) {
            let selectedOgranizationData = _.cloneDeep(orgs);
            const filteredData= selectedOgranizationData[0].childScopes?.length ?
                recursiveSearch("childScopes", [selectedOgranizationData[0]], activeFilter, orgs.id) : [];
            scopesData = filteredData.length ? filteredData : [];
        } else {
            scopesData = orgs;
        }
        // Apply folder-only filter to exclude projects
        return filterFoldersOnly(scopesData);
    }, [activeFilter, orgs, filterFoldersOnly]);

    const onChangeEvent = (scope) => {
        setSelectedScope(scope[0]);
    }

    // Helper function to find an element by id in the original unfiltered data
    const findElementById = useCallback((scopes, id) => {
        for (const scope of scopes) {
            if (scope.id === id) {
                return scope;
            }
            if (scope.childScopes?.length) {
                const found = findElementById(scope.childScopes, id);
                if (found) return found;
            }
        }
        return null;
    }, []);

    // Helper function to check if a folder has any DIRECT child projects (not recursive)
    // Only immediate parent folders of projects should be selectable, not grandparent folders
    const hasDirectChildProjects = useCallback((element) => {
        // Find the element in original orgs data to check for projects
        const originalElement = findElementById(orgs, element.id) || element;
        
        if (!originalElement.childScopes || originalElement.childScopes.length === 0) {
            return false;
        }
        // Only check for direct child projects, not recursively through child folders
        return originalElement.childScopes.some(child => 
            _.toLower(child.resourceType) === "project"
        );
    }, [orgs, findElementById]);

    const disableElement = useCallback((ele) => {
        const resourceType = _.toLower(ele.resourceType);
        // Disable organization if it doesn't have direct child projects
        if (resourceType === "organization") {
            return !hasDirectChildProjects(ele);
        }
        // Disable folder if it doesn't have any DIRECT child projects
        // Grandparent folders should not be selectable
        if (resourceType === "folder") {
            return !hasDirectChildProjects(ele);
        }
        return false;
    }, [hasDirectChildProjects]);

    const iconRenderer = (element) => {
        if (element.resourceType === "organization") {
            return <CompanyIcon className={element?.isSelected ? "" : styles["tenancy-icon-color"]} width="20"/>
        } else if (element.resourceType === "folder") {
            return <FolderIcon className={element?.isSelected ? "" : styles["tenancy-icon-color"]} width="20"/>
        }
        return null
    }

    const customStyles = {
        treeItemBase: {
            height: '55px'
        },
        treeListBase: {
            maxHeight: 'unset'
        }
    }

    // Helper function to find the first folder with direct child projects
    const findFirstFolderWithProjects = useCallback((scopes) => {
        for (const scope of scopes) {
            if (_.toLower(scope.resourceType) === "folder" && hasDirectChildProjects(scope)) {
                return scope;
            }
            if (scope.childScopes?.length) {
                const found = findFirstFolderWithProjects(scope.childScopes);
                if (found) return found;
            }
        }
        return null;
    }, [hasDirectChildProjects]);

    const selectedElement = useMemo(() => {
        // If activeScope exists and is an organization or folder, use it
        if (activeScope) {
            const resourceType = _.toLower(activeScope.resourceType);
            if (resourceType === "organization" || resourceType === "folder") {
                // Find the matching element in filteredScopes to ensure it exists
                const matchingElement = findElementById(filteredScopes, activeScope.id);
                if (matchingElement) {
                    return [matchingElement];
                }
            }
        }
        // Default to organization as the selected scope if it has direct child projects
        if (filteredScopes.length && _.toLower(filteredScopes[0]?.resourceType) === "organization") {
            // Check if org has direct child projects
            if (hasDirectChildProjects(filteredScopes[0])) {
                return [filteredScopes[0]];
            }
            // Org doesn't have direct child projects, find first folder with projects
            const firstFolderWithProjects = findFirstFolderWithProjects(filteredScopes[0]?.childScopes || []);
            if (firstFolderWithProjects) {
                const matchingElement = findElementById(filteredScopes, firstFolderWithProjects.id);
                if (matchingElement) {
                    return [matchingElement];
                }
            }
            // Fall back to org if no folder with projects found
            return [filteredScopes[0]];
        }
        // Fall back to the first available scope
        return filteredScopes.length ? [filteredScopes[0]] : [];
    }, [filteredScopes, activeScope, findElementById, hasDirectChildProjects, findFirstFolderWithProjects]);

    return <div style={{display: "inline"}}>
        <SearchWidget color="secondary" className={serviceConnectorMenuStyles['search-widget-container']} alwaysOpen={true} placeholder={t("search")} setFilter={setActiveFilter} widgetClassName={serviceConnectorMenuStyles['search-widget']}/>
        <div className={styles["dropdown-icon"]} style={{margin: "15px 40px 0px 40px", overflowY: 'auto', height: '85%'}}>
            <TreeList
                treeData={filteredScopes}
                menuType={'singleRadio'}
                uniqueIdentifier={'id'}
                labelKey={'name'}
                bottomBorder={true}
                childKey={'childScopes'}
                selectedValues={selectedElement}
                onChange={onChangeEvent}
                expandLevel={"max"}
                isOptionDisabled={disableElement}
                iconRenderer={iconRenderer}
                customStyles={customStyles}
                dropdownUpIcon={() => <DropdownArrowUpIcon
                    className={styles["tenancy-icon-color"]}/>} 
                dropdownDownIcon={() => <DropdownArrowDownIcon
                    className={styles["tenancy-icon-color"]}/>}
                    />
        </div>
    </div>
});

export default () => {
    const location = useLocation();
    const activeScope = useSelector(state => state.tenancy.selectedScope);
    const scopesFullyLoaded = useSelector(state => state.tenancy.scopesFullyLoaded);
    const selectedOrg = useSelector(state => state.tenancy.selectedOrg)
    const [isDisabled, setIsDisabled] = useState(true);
    const [selectedScope, setSelectedScope] = useState(activeScope);
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);
    const isFleetManagementEnabled = useFeatureFlag("NetApp.FleetManagement/*");
    const scopeName = (isFleetManagementEnabled && selectedOrg?.name.toLowerCase() === "fabricam") ? "Production" : activeScope?.name || t("not_applicable");

    // Sync selectedScope with activeScope when it changes
    useEffect(() => {
        if (activeScope) {
            setSelectedScope(activeScope);
        }
    }, [activeScope]);

    useEffect(() => {
        if (!_.isUndefined(activeScope)) {
            let scopeId = activeScope.id; 
           
            dispatch({
                type: "TENANCYV4:SET-PERMISSIONS",
                payload: {
                    scopeId
                }
            })
        setIsDisabled(location.pathname.includes("/identity-access-mgmt"))  
        }              

    }, [location, selectedOrg, activeScope, dispatch]);

    const toggleHandler = (isActive) => {
        if(!isActive && !scopesFullyLoaded){
            dispatch({
                type: "TENANCYV4:FETCH-SCOPES",
                payload: {
                    loadSingleScope: false,
                }
            })
        }
    }

    return <SlidingWidget
        widgetKey="scope"
        menuTitle={isFleetManagementEnabled ? t("scopes") : t("projects")}
        triggerDisabled={isDisabled}
        canClose={() => {
            return false;
        }}
        triggerClassName={styles['caption-button']}
        triggerActiveClassName={[styles['caption-button-active']]}
        onToggle={toggleHandler}
        TriggerButton={(props) => <CaptionButton isDisabled={isDisabled} title={isFleetManagementEnabled ? t("scope") : t("project")} value={scopeName} {...props}/>}
        MenuFooter={() => <ScopeMenuFooter  selectedScope={selectedScope}/>}
    >
        {scopesFullyLoaded ? <MenuContent selectedScope={selectedScope} setSelectedScope={setSelectedScope}/> : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}><InlineLoader/></div>}
    </SlidingWidget>
};